import React from 'react'
import dayjs from 'dayjs'
import CloseIcon from '@mui/icons-material/Close'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import LocationOnIcon from '@mui/icons-material/LocationOn'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import BadgeIcon from '@mui/icons-material/Badge'
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety'
import InfoIcon from '@mui/icons-material/Info'
import VerifiedIcon from '@mui/icons-material/Verified'
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt'
import BlockIcon from '@mui/icons-material/Block'
import VisibilityIcon from '@mui/icons-material/Visibility'
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Grid,
  Chip,
  Card,
  CardContent,
  Typography,
  Alert,
  Button,
  Box,
  DialogActions,
  Divider,

} from '@mui/material'
import NoAccountsIcon from '@mui/icons-material/NoAccounts'

const formatDateMMDDYY = (date: string | Date | null | undefined) => {
  if (!date) return '--'
  const dayjsDate = dayjs(date)
  if (!dayjsDate.isValid()) return '--'
  return dayjsDate.format('MM/DD/YYYY')
}

export interface AgencyDetailsDialogProps {
  open: boolean
  onClose: () => void
  provider: any
  actions?: React.ReactNode
}

const AgencyDetailsDialog: React.FC<AgencyDetailsDialogProps> = ({
  open,
  onClose,
  provider,
  actions,
}) => {
  if (!provider) return null

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          maxHeight: '90vh',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 2,
          pt: 3,
          px: 3,
          background: 'linear-gradient(135deg, #5BC0BE 0%, #4a9d9b 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PersonOutlineIcon sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, letterSpacing: 0.5 }}
            >
              Agency Details
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'white',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.2)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{ pt: '1rem !important', pb: 2, px: 3, bgcolor: '#f8f9fa' }}
      >
        <Grid container spacing={3}>
          {/* Profile Section */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <PersonOutlineIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Agency Information
                  </Typography>

                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Name
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '1rem',
                      }}
                    >
                      {provider?.agencyName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Website
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider?.website || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Email
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider?.companyEmail || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyPhone || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency States
                    </Typography>



                    {provider.companyStates.map(state => (
                      <Chip
                        label={state}
                        size="small"
                        sx={{
                          fontWeight: 600,
                          height: 28,
                          '& .MuiChip-label': {
                            px: 1.5,
                          },
                          mx: 0.5
                        }}
                      />))}


                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      No of Provider
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.noOfProvider || '--'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Status
                    </Typography>
                    <Chip
                      label={provider.isActive ? 'Active' : 'Inactive'}
                      color={provider.isActive === true ? 'success' : 'error'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>

                  {provider.bio && (
                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6c757d',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Bio
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 0.5,
                          color: '#495057',
                          lineHeight: 1.7,
                          bgcolor: '#f8f9fa',
                          p: 1.5,
                          borderRadius: 1,
                        }}
                      >
                        {provider.bio}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Address Section */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <LocationOnIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Address Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Address Line
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.addressLine1 || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Address Line
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.addressLine2 || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      City
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.city || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      State
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.state || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Zip Code
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.zipCode || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Country
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.country || '--'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Billing Information */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <AccountBalanceIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Billing Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Agency Npi
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyNpi || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Billing NPI
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.billingNpi || '--'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      EIN
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.companyEin || '--'}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Tax ID
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.taxId || '--'}
                    </Typography>
                  </Grid>

                  {provider.companyName && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: '#6c757d',
                          fontWeight: 600,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                          fontSize: '0.7rem',
                          mb: 0.5,
                          display: 'block',
                        }}
                      >
                        Company Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 500,
                          color: '#495057',
                          fontSize: '0.95rem',
                        }}
                      >
                        {provider.companyName}
                      </Typography>
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Billing Phone
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: '#495057',
                        fontSize: '0.95rem',
                      }}
                    >
                      {provider.billingPhone || '--'}
                    </Typography>
                  </Grid>
                  {provider.billingAddressLine && (
                    <>
                      <Grid item xs={12}>
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#6c757d',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.5,
                            fontSize: '0.7rem',
                            mb: 0.5,
                            display: 'block',
                          }}
                        >
                          Billing Address
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            fontWeight: 500,
                            color: '#495057',
                            fontSize: '0.95rem',
                          }}
                        >
                          {provider.billingAddressLine}
                          {provider.billingCity && `, ${provider.billingCity}`}
                          {provider.billingState &&
                            `, ${provider.billingState}`}
                          {provider.billingZipCode &&
                            ` ${provider.billingZipCode}`}
                        </Typography>
                      </Grid>
                    </>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Licenses Section */}
          {provider.licenses && provider.licenses.length > 0 && (
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      mb: 3,
                      pb: 2,
                      borderBottom: '2px solid',
                      borderColor: '#5BC0BE',
                    }}
                  >
                    <BadgeIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        color: '#2c3e50',
                        letterSpacing: 0.3,
                      }}
                    >
                      License Information
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {provider.licenses.map((license: any, index: number) => (
                      <Grid item xs={12} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            p: 2.5,
                            borderRadius: 2,
                            border: '1px solid',
                            borderColor: '#e9ecef',
                            bgcolor: '#f8f9fa',
                          }}
                        >
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Number
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 600,
                                  color: '#2c3e50',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.licenseNumber || '--'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License State
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: '#495057',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.licenseState || '--'}
                              </Typography>
                            </Grid>

                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                Expiry Date
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  fontWeight: 500,
                                  color: '#495057',
                                  fontSize: '0.95rem',
                                }}
                              >
                                {license.expiryDate
                                  ? formatDateMMDDYY(license.expiryDate)
                                  : '--'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Status
                              </Typography>
                              <Chip
                                label={license.licenseStatus || '--'}
                                color={
                                  license.licenseStatus === 'Active'
                                    ? 'success'
                                    : 'default'
                                }
                                size="small"
                                sx={{
                                  fontWeight: 600,
                                  height: 28,
                                  '& .MuiChip-label': {
                                    px: 1.5,
                                  },
                                }}
                              />
                            </Grid>
                            <Grid item xs={12}>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: '#6c757d',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: 0.5,
                                  fontSize: '0.7rem',
                                  mb: 0.5,
                                  display: 'block',
                                }}
                              >
                                License Document
                              </Typography>
                              {license.licenseFileUrl ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  href={license.licenseFileUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  startIcon={<VisibilityIcon />}
                                  sx={{
                                    textTransform: 'none',
                                    mt: 0.5,
                                  }}
                                >
                                  View License Document
                                </Button>
                              ) : (
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#6c757d',
                                    fontStyle: 'italic',
                                    mt: 0.5,
                                  }}
                                >
                                  No document available
                                </Typography>
                              )}
                            </Grid>
                          </Grid>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Insurance Accepted */}
          {provider.insuranceAccepted &&
            provider.insuranceAccepted.length > 0 && (
              <Grid item xs={12}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 3,
                        pb: 2,
                        borderBottom: '2px solid',
                        borderColor: '#5BC0BE',
                      }}
                    >
                      <HealthAndSafetyIcon
                        sx={{ color: '#5BC0BE', fontSize: 28 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: '#2c3e50',
                          letterSpacing: 0.3,
                        }}
                      >
                        Accepted Insurances
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                      {provider.insuranceAccepted.map(
                        (insurance: any, index: number) => (
                          <Chip
                            key={index}
                            label={insurance.name || '--'}
                            sx={{
                              bgcolor: '#e3f2fd',
                              color: '#1976d2',
                              fontWeight: 600,
                              border: '1px solid #90caf9',
                              '&:hover': {
                                bgcolor: '#bbdefb',
                              },
                            }}
                            size="medium"
                          />
                        )
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            )}

          {/* Additional Information */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'white',
                transition: 'all 0.3s ease',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: '#5BC0BE',
                  }}
                >
                  <InfoIcon sx={{ color: '#5BC0BE', fontSize: 28 }} />
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: '#2c3e50',
                      letterSpacing: 0.3,
                    }}
                  >
                    Additional Information
                  </Typography>
                </Box>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Bank Connected
                    </Typography>
                    <Chip
                      label={provider.isConnectBank ? 'Yes' : 'No'}
                      color={provider.isConnectBank ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Subscription Taken
                    </Typography>
                    <Chip
                      label={provider.isSubscriptionTaken ? 'Yes' : 'No'}
                      color={
                        provider.isSubscriptionTaken ? 'success' : 'default'
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Service Created
                    </Typography>
                    <Chip
                      label={provider.isServiceCreated ? 'Yes' : 'No'}
                      color={provider.isServiceCreated ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Slot Added
                    </Typography>
                    <Chip
                      label={provider.isSlotAdded ? 'Yes' : 'No'}
                      color={provider.isSlotAdded ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Accepts New Patients
                    </Typography>
                    <Chip
                      label={provider.isAcceptNewPatientEnq ? 'Yes' : 'No'}
                      color={
                        provider.isAcceptNewPatientEnq ? 'success' : 'default'
                      }
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      HIPAA Signed
                    </Typography>
                    <Chip
                      label={provider.hasSignedHIPAA ? 'Yes' : 'No'}
                      color={provider.hasSignedHIPAA ? 'success' : 'default'}
                      size="small"
                      sx={{
                        fontWeight: 600,
                        height: 28,
                        '& .MuiChip-label': {
                          px: 1.5,
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Onboarding Date
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '0.95rem',
                      }}
                    >
                      {formatDateMMDDYY(provider.createdAt) || '--'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6c757d',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '0.7rem',
                        mb: 0.5,
                        display: 'block',
                      }}
                    >
                      Last Updated
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 600,
                        color: '#2c3e50',
                        fontSize: '0.95rem',
                      }}
                    >
                      {formatDateMMDDYY(provider.updatedAt) || '--'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </DialogContent>
      {actions && (
        <>
          <Divider sx={{ borderColor: '#e9ecef' }} />
          <DialogActions
            sx={{
              px: 3,
              py: 2,
              bgcolor: '#f8f9fa',
              justifyContent: 'flex-end',
              gap: 1.5,
            }}
          >
            {actions}
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

export default AgencyDetailsDialog
